import React, { useEffect, useState, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Button, CircularProgress } from '@mui/material'
import { useYupValidationResolver } from 'hooks/useYupValidationResolver'
import { getTokenData, setTokenData } from 'util/countdown-store' // Updated IndexedDB functions
import CodeInput from 'components/CodeInput'
import { generateTokenService, validateTokenService } from 'services/token'
import { useTurnstile } from 'hooks/useTurnstile'
import { Turnstile } from '@marsidev/react-turnstile'

import * as yup from 'yup'
import * as styles from './UserVerificationForm.module.scss'

export const UserVerificationForm = ({
  email = 'a@gmail.com',
  regTurnstileToken,
  firstName,
  lastName,
  goToInfo,
  onConfirm,
  verificationCodeExpiration = 900, // Test with 30 seconds
  manualResendCooldown = 180, // 3 minutes
  hasTurnstile = false,
  formClassName,
  titleClassName = styles.header,
  // Custom token services - allows parent components to override default behavior
  customGenerateTokenService = null, // Optional: custom service for token generation (e.g., with turnstile)
  customValidateTokenService = null, // Optional: custom service that can validate + perform action (e.g., generate barcode)
  tokenServicePayload = {}, // Optional: additional payload to pass to custom services
  skipAutoTokenGeneration = false, // Optional: skip automatic token generation in useEffect (useful when you want manual control)
  onTokenAlreadyRequested = null, // Optional: callback when token request is blocked (e.g. already requested)
  onValidationError = null, // Optional: callback when validation/barcode generation fails
}) => {
  const [isGettingTime, setGettingTime] = useState(true)
  const [isInProgress, setIsInProgress] = useState(false)
  const [secondsToResend, setSecondsToResend] = useState(
    verificationCodeExpiration,
  )
  const { turnstileRef, getTurnstileToken, setTurnstileToken } = useTurnstile()
  const [manualCooldown, setManualCooldown] = useState(manualResendCooldown)
  const intervalRef = useRef(null)
  const [turnstileTokenReady, setTurnstileTokenReady] = useState(false)
  const [codeInputValid, setCodeInputValid] = useState(false)
  const [tokenSentOnMount, setTokenSentOnMount] = useState(false)

  const yupResolver = yup.object().shape({
    codeInput: yup
      .string()
      .required('Code is required')
      .typeError('Code is required'),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    resolver: useYupValidationResolver(yupResolver),
  })

  const codeInput = register('codeInput', { required: true })

  const handleCodeInputChange = input => {
    codeInput.onChange({
      target: {
        value: input,
        name: 'codeInput',
      },
    })
    setCodeInputValid(input && input.length === 6)
  }

  const memoizedEmail = React.useMemo(() => email, [email])

  const sendToken = async (withTurnstile = false) => {
    // Use custom token generation service if provided, otherwise use default
    const tokenService = customGenerateTokenService || generateTokenService

    const basePayload = {
      firstName,
      lastName,
      tokenSource: 'RECEIPT_UPLOAD',
      email: memoizedEmail,
      type: 'EMAIL',
    }

    // Build the final payload
    const finalPayload = {
      ...basePayload,
      ...(tokenServicePayload || {}), // This may include turnstileToken from parent form
    }

    // If turnstile is required from modal and not already in payload
    const hasTokenInPayload =
      tokenServicePayload &&
      Object.prototype.hasOwnProperty.call(
        tokenServicePayload,
        'turnstileToken',
      ) &&
      tokenServicePayload['turnstileToken']
    if (withTurnstile && hasTurnstile && !hasTokenInPayload) {
      const modalTurnstileToken = await getTurnstileToken()
      if (modalTurnstileToken) {
        finalPayload['turnstileToken'] = modalTurnstileToken
      }
    }

    // Add regTurnstileToken if present and no other turnstile token
    if (
      regTurnstileToken &&
      !Object.prototype.hasOwnProperty.call(finalPayload, 'turnstileToken')
    ) {
      finalPayload['turnstileToken'] = regTurnstileToken
    }


    try {
      await tokenService(finalPayload)
    } catch (error) {
      const status = error?.status
      const detail = (error?.detail || error?.message || '').toString()
      const detailLower = detail.toLowerCase()

      const looksAlreadyRequested =
        status === 409 ||
        status === 429 ||
        detailLower.includes('already') ||
        detailLower.includes('already been sent') ||
        detailLower.includes('already sent') ||
        detailLower.includes('already requested')

      if (looksAlreadyRequested) {
        if (typeof onTokenAlreadyRequested === 'function') {
          onTokenAlreadyRequested()
        }

        // Prevent repeated attempts while the dialog is open.
        // Also update local timers so UI doesn't encourage spamming resend.
        const now = Date.now()
        await setTokenData(memoizedEmail, {
          tokenExpiresAt: now + verificationCodeExpiration * 1000,
          canSendAnNewTokenAt: now + manualResendCooldown * 1000,
        })

        return
      }

      throw error
    }

    const now = Date.now()
    await setTokenData(memoizedEmail, {
      tokenExpiresAt: now + verificationCodeExpiration * 1000,
      canSendAnNewTokenAt: now + manualResendCooldown * 1000,
    })
  }

  useEffect(() => {
    const initializeTokenFlow = async () => {
      const tokenData = await getTokenData(memoizedEmail)
      const now = Date.now()

      // Auto-generate token if needed
      // Skip if:
      // 1. skipAutoTokenGeneration is true
      // 2. Using custom service (will be handled by second useEffect)
      if (
        !skipAutoTokenGeneration &&
        !customGenerateTokenService &&
        (!tokenData || tokenData.tokenExpiresAt <= now)
      ) {
        await sendToken(false) // Send without turnstile (default service only)
      }

      setSecondsToResend(
        tokenData
          ? Math.max(0, (tokenData.tokenExpiresAt - now) / 1000)
          : verificationCodeExpiration,
      )
      setManualCooldown(
        tokenData
          ? Math.max(0, (tokenData.canSendAnNewTokenAt - now) / 1000)
          : manualResendCooldown,
      )
    }

    initializeTokenFlow()

    intervalRef.current = setInterval(async () => {
      const tokenData = await getTokenData(memoizedEmail)
      const now = Date.now()

      if (tokenData) {
        const timeToResend = Math.max(
          0,
          (tokenData.tokenExpiresAt - now) / 1000,
        )
        const timeToManualResend = Math.max(
          0,
          (tokenData.canSendAnNewTokenAt - now) / 1000,
        )

        setSecondsToResend(timeToResend)
        setManualCooldown(timeToManualResend)
      }
    }, 1000)

    return () => clearInterval(intervalRef.current)
  }, [memoizedEmail, skipAutoTokenGeneration, customGenerateTokenService])

  useEffect(() => {
    setTimeout(() => setGettingTime(false), 1500)
  }, [])

  // Auto-send token when ready (only for custom services)
  // For InComm flow: turnstileToken is passed via tokenServicePayload (from form)
  // For other flows with hasTurnstile: wait for modal's turnstile to be ready
  useEffect(() => {
    const autoSendToken = async () => {
      // Only proceed if using custom service
      if (
        !customGenerateTokenService ||
        tokenSentOnMount ||
        skipAutoTokenGeneration
      ) {
        return
      }

      // If turnstileToken is already in payload (from parent form), we can send immediately
      const hasTurnstileInPayload =
        tokenServicePayload &&
        Object.prototype.hasOwnProperty.call(
          tokenServicePayload,
          'turnstileToken',
        ) &&
        !!tokenServicePayload.turnstileToken

      // Wait for turnstile if needed (modal has turnstile and no token in payload)
      const shouldWaitForTurnstile = hasTurnstile && !hasTurnstileInPayload
      if (shouldWaitForTurnstile && !turnstileTokenReady) {
        return // Wait for turnstile to be ready
      }

      const tokenData = await getTokenData(memoizedEmail)
      const now = Date.now()

      // Only send if no valid token exists
      if (!tokenData || tokenData.tokenExpiresAt <= now) {
        await sendToken(shouldWaitForTurnstile) // Get turnstile from modal only if not in payload
        // Mark as sent/attempted even if backend blocked (already requested) to avoid loops
        setTokenSentOnMount(true)
      }
    }

    autoSendToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customGenerateTokenService,
    tokenSentOnMount,
    skipAutoTokenGeneration,
    hasTurnstile,
    turnstileTokenReady,
    memoizedEmail,
  ])

  const onSubmit = async (data, e) => {
    e.preventDefault()
    setIsInProgress(true)

    try {
      // If using custom validation service (e.g., validate + generate barcode)
      if (customValidateTokenService) {
        // For InComm flow: Turnstile was already validated at token generation time
        // No need to send it again for barcode generation
        const confirmationData = {
          email: memoizedEmail,
          token: data.codeInput,
          type: 'EMAIL',
          ...tokenServicePayload, // Include additional payload (e.g., offerId, selectedBannerStoreId)
        }

        // Custom service validates and performs action (e.g., generate barcode)
        // Returns the result data (e.g., barcode response)
        const response = await customValidateTokenService(confirmationData)

        if (response) {
          onConfirm({
            confirmed: true,
            values: {
              confirmed: true,
              ...response, // Pass the entire response (e.g., barcode data) to parent
            },
          })
        } else {
          setError('codeInput', { type: 'manual', message: 'Invalid code' })
        }
      } else {
        // Default validation service (just validates token, returns boolean)
        const confirmationData = {
          tokenSource: 'RECEIPT_UPLOAD',
          email: memoizedEmail,
          token: data.codeInput,
          type: 'EMAIL',
        }

        const isValid = await validateTokenService(confirmationData)

        if (isValid && hasTurnstile) {
          const turnstileToken = await getTurnstileToken()
          if (!turnstileToken) {
            setError('turnstile', {
              type: 'manual',
              message: 'Failed to verify you are human. Please try again.',
            })
            setIsInProgress(false)
            return
          }

          onConfirm({
            confirmed: true,
            values: {
              confirmed: true,
              turnstileToken,
            },
          })
        } else if (isValid) {
          onConfirm({
            confirmed: true,
            values: {
              confirmed: true,
            },
          })
        } else {
          setError('codeInput', { type: 'manual', message: 'Invalid code' })
        }
      }
    } catch (error) {
      console.error('Validation error:', error)
      
      // If using custom validation service and error callback is provided, call it
      if (customValidateTokenService && typeof onValidationError === 'function') {
        onValidationError(error)
      } else {
        // Otherwise show error in form
        setError('codeInput', {
          type: 'manual',
          message:
            error?.detail?.message || 'Validation failed. Please try again.',
        })
      }
    }

    setIsInProgress(false)
  }

  return (
    <FormProvider>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={formClassName ? formClassName : styles.formWrapper}
      >
        <div className={styles.wrapper}>
          <span className={titleClassName}>Let's get you verified!</span>
          <div className={styles.content}>
            <span>
              A verification code has been sent to your email. Please check your
              inbox and enter the code below.
            </span>
            <div className={styles.emailInfo}>
              <span>Email Linked to Your Account</span>
              <span className={styles.email}>{`*${email}`}</span>
            </div>
            <div className={styles.codeArea}>
              <label htmlFor="codeInput">Enter the 6 digit code</label>
              <CodeInput
                allowedCharacters="numeric"
                width={32}
                height={52}
                ref={codeInput.ref}
                onChange={handleCodeInputChange}
              />
              {errors.codeInput && (
                <span className="error-message">
                  {errors.codeInput.message}
                </span>
              )}
              <p
                style={{
                  marginBottom: '0px',
                }}
              >
                {isGettingTime ? (
                  <CircularProgress size={10} />
                ) : secondsToResend > 0 ? (
                  `This code expires in
                ${new Date(secondsToResend * 1000).toISOString().substr(14, 5)} 
                ${secondsToResend > 60 ? 'minutes.' : 'seconds.'}`
                ) : (
                  'Code expired. Please request a new one.'
                )}
              </p>

              {hasTurnstile && (
                <Turnstile
                  ref={turnstileRef}
                  options={{
                    refreshExpired: 'manual',
                  }}
                  siteKey={process.env.VISIBLE_TURNSTILE_SITE_KEY}
                  onSuccess={token => {
                    setTurnstileToken(token)
                    setTurnstileTokenReady(true)
                  }}
                  onError={error => {
                    console.error('Turnstile error:', error)
                    setTurnstileToken(null)
                    setTurnstileTokenReady(false)
                  }}
                  onExpire={() => {
                    console.log('Turnstile token expired')
                    setTurnstileToken(null)
                    setTurnstileTokenReady(false)
                    turnstileRef.current?.reset()
                  }}
                />
              )}
              {errors.turnstile && (
                <span className="error-message">
                  {errors.turnstile.message}
                </span>
              )}
              <Button
                disabled={
                  isInProgress ||
                  (hasTurnstile && !turnstileTokenReady) ||
                  !codeInputValid
                }
                variant="contained"
                type="submit"
              >
                {isInProgress ? <CircularProgress size={18} /> : 'Confirm Code'}
              </Button>
            </div>
            {isGettingTime ? (
              <CircularProgress style={{ alignSelf: 'center' }} size={10} />
            ) : !isGettingTime && manualCooldown > 0 ? (
              <p>
                Haven't received a code in your inbox? You can request a new one
                in {new Date(manualCooldown * 1000).toISOString().substr(14, 5)}{' '}
                {manualCooldown > 60 ? 'minutes.' : 'seconds.'}
              </p>
            ) : (
              <button
                disabled={isInProgress || manualCooldown > 0}
                onClick={goToInfo}
                className={styles.resendLink}
              >
                I didn't receive an email
              </button>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
